import type {
  ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
} from "@amplecare/contracts";

export type QuestionnaireReviewFilterId =
  | "all"
  | "attention"
  | "changed"
  | "medications";

export const questionnaireReviewAllSectionsKey = "__all_sections__";

export interface QuestionnaireReviewSection {
  attentionCount: number;
  changedCount: number;
  entries: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[];
  key: string;
  labelText: string;
  medicationCount: number;
}

function normalizeSectionLabel(value: string | null | undefined): string | null {
  const normalized = value?.replace(/\s+/gu, " ").trim();

  return normalized ? normalized : null;
}

function normalizeSectionKeyForLabel(
  value: string | null | undefined,
): string | null {
  const normalized = value?.replace(/[_-]+/gu, " ").replace(/\s+/gu, " ").trim();

  return normalized ? normalized : null;
}

function titleCase(value: string): string {
  return value.replace(/\b[a-z]/gu, (character) => character.toUpperCase());
}

function inferLegacySection(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): { key: string; labelText: string } {
  const questionText =
    `${entry.questionKey} ${entry.promptText}`.toLowerCase();

  if (
    /consent|privacy|confirm.*patient|answering.*yourself|good.*time/iu.test(
      questionText,
    )
  ) {
    return { key: "call-start", labelText: "Call start and consent" };
  }

  if (/medication|medicine|blood.thinner|anticoag/iu.test(questionText)) {
    return { key: "medications", labelText: "Medications" };
  }

  if (/allerg/iu.test(questionText)) {
    return { key: "allergies", labelText: "Allergies" };
  }

  if (/ride|transport|support.person|caregiver/iu.test(questionText)) {
    return { key: "ride-home", labelText: "Ride home and post-surgery care" };
  }

  if (/procedure|surgery|surgeon|anesth|fasting/iu.test(questionText)) {
    return {
      key: "surgery-context",
      labelText: "Surgery and anesthesia",
    };
  }

  if (
    /cardiac|pacemaker|pulmonary|renal|hepatic|neurolog|gastro|endocr|oncolog|hematolog|musculoskeletal|psychiatric|symptom/iu.test(
      questionText,
    )
  ) {
    return { key: "health-review", labelText: "Health review" };
  }

  return { key: "other-answers", labelText: "Other answers" };
}

export function questionnaireAnswerNeedsAttention(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): boolean {
  if (
    entry.reviewStatus === "reviewed" ||
    entry.reviewStatus === "not_required"
  ) {
    return false;
  }

  return (
    entry.redFlagTriggered ||
    entry.clinicianReviewRequired ||
    entry.reviewSignal === "red_flag" ||
    entry.reviewSignal === "yellow_flag" ||
    entry.definitionRequested === true ||
    (entry.definitionRequestCount ?? 0) > 0
  );
}

export function questionnaireAnswerChanged(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): boolean {
  return (
    entry.answerStatus === "changed" ||
    entry.answerStatus === "redacted" ||
    entry.changedSinceFirstAnswer === true ||
    (entry.answerEvents ?? []).some(
      (event) =>
        event.eventType === "answer_changed" ||
        event.eventType === "answer_redacted",
    )
  );
}

export function questionnaireAnswerHasMedicationDetails(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): boolean {
  return (
    (entry.medicationEntries?.length ?? 0) > 0 ||
    (entry.medicationEntryCount ?? 0) > 0 ||
    Boolean(entry.medicationReconciliationStatus)
  );
}

export function questionnaireAnswerMatchesFilter(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  filterId: QuestionnaireReviewFilterId,
): boolean {
  switch (filterId) {
    case "attention":
      return questionnaireAnswerNeedsAttention(entry);
    case "changed":
      return questionnaireAnswerChanged(entry);
    case "medications":
      return questionnaireAnswerHasMedicationDetails(entry);
    case "all":
    default:
      return true;
  }
}

export function questionnaireReviewHasHiddenAttention(
  allEntries: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[],
  visibleEntries: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[],
): boolean {
  return (
    visibleEntries.filter(questionnaireAnswerNeedsAttention).length <
    allEntries.filter(questionnaireAnswerNeedsAttention).length
  );
}

export function buildQuestionnaireReviewSections(
  entries: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[],
): QuestionnaireReviewSection[] {
  const sectionsByKey = new Map<string, QuestionnaireReviewSection>();

  for (const entry of entries) {
    const explicitKey = entry.sectionKey?.trim() || null;
    const explicitLabel = normalizeSectionLabel(entry.sectionLabel);
    const fallbackSection = inferLegacySection(entry);
    const key = explicitKey
      ? `persisted:${explicitKey}`
      : `legacy:${fallbackSection.key}`;
    const labelText =
      explicitLabel ??
      (explicitKey
        ? titleCase(normalizeSectionKeyForLabel(explicitKey) ?? explicitKey)
        : fallbackSection.labelText);
    const existing = sectionsByKey.get(key) ?? {
      attentionCount: 0,
      changedCount: 0,
      entries: [],
      key,
      labelText,
      medicationCount: 0,
    };

    existing.entries.push(entry);
    existing.attentionCount += questionnaireAnswerNeedsAttention(entry) ? 1 : 0;
    existing.changedCount += questionnaireAnswerChanged(entry) ? 1 : 0;
    existing.medicationCount += questionnaireAnswerHasMedicationDetails(entry)
      ? 1
      : 0;
    sectionsByKey.set(key, existing);
  }

  return [...sectionsByKey.values()];
}
